// B1 — WHAT A MEMBER MAY DO WITH A VIDEO, AND IT IS ONE LAW BECAUSE TWO SPELLINGS OF IT ARE WHAT
// PUT TWO LOAD-BEARING COMMENTS IN THIS REPO INTO CONTRADICTION FOR MONTHS.
//
// This module was `src/lib/video-access.ts`, which is CLIENT-ONLY by that directory's own entry
// requirement — so the SERVER could not read it and answered the same question in its own words one
// layer above its own handler. It is a shared `.js` now, exactly as `src/funnelCapture.js` holds the
// funnel clock that `src/lib/funnel-prefill.ts` re-exports, and `src/lib/video-access.ts` re-exports
// these two under their original names so nothing that imports them has to know they moved (R165:
// what is gone is the BODY, never the name a reader looks for).
//
// ---- THE CONTRADICTION, AND WHICH HALF WAS WRONG (resolved by DRIVING the door, never by reading
// either comment — L12/CRYSTAL: a fixture quoting an inline shape certifies the spelling, not the
// behaviour) ----
//
// This file used to state, as the reason its whole law exists: *"`handleVideoStream` gates a product
// video on `hasPurchase` ALONE — there is no tier check on that path at all — so a lapsed member who
// bought Spirit Keeping can stream it."* **That is true of the HANDLER and false of the SERVER.**
// `featureForRoute("GET", "/api/video/stream/spirit-keeping")` answers `"library"` — driven, not
// read — and `server/index.js` applies that table CENTRALLY, before dispatch, so a lapsed buyer was
// refused 402 one layer above the handler that would have let her in.
//
// **SO T1-7 — *bought once, hers for good* — WAS BROKEN ON THE SERVER, and the CARD was the surface
// telling the truth.** Measured on the live table before a line was written (R25/R180): 11 purchase
// rows, 3 on real members, all three `spirit-keeping`, and **all three currently hold an entitling
// subscription — so the defect is REACHABLE and has cost nobody yet** (R141/R19: it is not reported
// as somebody's live loss). It goes live the first time one of those three lapses.
//
// **THE REPAIR IS THE CROSSING RATHER THAN EITHER SPELLING.** The route is declared free in
// `server/route-features.js` with a per-VIDEO check inside the handler — the same shape that table
// already uses for `/api/room/generate`, `/api/lala/craft` and `/api/manual/`, and for the same
// reason: only the handler knows WHICH video is being asked for, and which video it is decides
// whether the question is a purchase or a tier. Both sides then run THIS function.
//
// It imports NOTHING, which is the entry requirement that lets a `.mjs` gate load and RUN it.

/**
 * What the server's `/api/membership/tiers` publishes for a one-time product. Only `key` is
 * load-bearing here; the rest is what the card and the cover popup render.
 * @typedef {{ key: string, label: string, priceText: string, blurb?: string, videoSlug?: string }} VideoProduct
 */

/**
 * THREE STATES, AND THEY ARE THE THREE FACES T8-9 ASKED FOR.
 *   play   — she can watch it now (hers by tier, or hers by purchase, forever)
 *   buy    — it carries a price she has not paid; the card wears the price, not a padlock
 *   locked — no price on it, and her tier does not include the library
 * @typedef {"play" | "buy" | "locked"} VideoState
 */

/**
 * Three answers were once hand-typed for "has she bought this" (`ownsProduct(product.key)`,
 * `Boolean(product) && !owned`, and `ownedProducts.includes(oneTimeFor(slug)?.key || "")` — that last
 * one asking whether she owns the product keyed by the empty string, correct only by accident).
 * @param {VideoProduct | null | undefined} product
 * @param {readonly string[]} ownedKeys
 * @returns {boolean}
 */
export const ownsVideoProduct = (product, ownedKeys) =>
  Boolean(product && product.key && Array.isArray(ownedKeys) && ownedKeys.includes(product.key));

/**
 * **A PRODUCT VIDEO NEVER CONSULTS THE TIER, IN EITHER DIRECTION.** Owning it is enough (T1-7), and
 * not owning it is refused with a PRICE rather than with an upgrade — W1-2's ruling, because
 * upgrading would not deliver it. That property is what lets the server skip the entitlement read
 * entirely for a priced video, on a handler that runs per RANGE REQUEST.
 * @param {VideoProduct | null | undefined} product
 * @param {boolean} owned
 * @param {boolean} canStream
 * @returns {VideoState}
 */
export function videoState(product, owned, canStream) {
  if (product) return owned ? "play" : "buy";
  return canStream ? "play" : "locked";
}
